import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ValueType } from 'react-select';

import { ITeamMemberMapped } from '~/utils/clientAllocation';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';

import ScrollIntoViewMenu from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/components/ScrollIntoViewMenu/ScrollIntoViewMenu';
import { ClientWithAllocation } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/types';
import { useAddClientAllocationContext } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/context/AddClientAllocationContext';
import { normalizeClientOptionsToAllocations } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/EditClientAllocation/utils';
import useSelectAdditionalStyles from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/hooks/useSelectAdditionalStyles';
import { IAddAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/types';
import { IOption } from '~/types';

interface IProps {
  teamMember: ITeamMemberMapped;
  teamMemberIdx: number;
  updateAllocations: (selectedClients: ClientWithAllocation[]) => void;
}

const AllocationClientsSelect: FC<IProps> = ({
  teamMember,
  teamMemberIdx,
  updateAllocations,
}): ReactElement => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<IAddAllocationFormValues>();
  const { clients } = useAddClientAllocationContext();

  const selectedFieldName = `teamMembersAllocations.${teamMemberIdx}.selectedClientsIds` as const;
  const { additionalStyleHandler } = useSelectAdditionalStyles(teamMemberIdx);

  const teamMembersAllocations = watch('teamMembersAllocations');

  const clientsAllocationsFields = useMemo(
    () => teamMembersAllocations[teamMemberIdx].clientsAllocations,
    [teamMembersAllocations, teamMemberIdx],
  );

  const groupedExistingClientsByClientId = useMemo(
    () => new Map(clientsAllocationsFields.map(field => [field.clientId, field])),
    [clientsAllocationsFields],
  );

  const handleSelectClientOptions = useCallback(
    (selected: ValueType<IOption | IOption[], boolean>) => {
      const options = (Array.isArray(selected) ? selected : []) as IOption[];

      updateAllocations(
        normalizeClientOptionsToAllocations(options, null).map(
          client => groupedExistingClientsByClientId.get(client.clientId) ?? client,
        ),
      );
    },
    [groupedExistingClientsByClientId, updateAllocations],
  );

  return (
    <SelectComponent
      name={selectedFieldName}
      control={control}
      errors={errors}
      closeMenuOnSelect={false}
      menuPortalTarget={null}
      menuPlacement="auto"
      isRelativeWindow={false}
      showCustomComponents
      hideSelectedOptions={false}
      hideMultiValueRemove
      additionalStyleHandler={additionalStyleHandler}
      isMulti
      label="Select Clients to Assign"
      options={clients}
      disableMenu={teamMember.isDeleted}
      customComponents={teamMember.isDeleted ? undefined : { Menu: ScrollIntoViewMenu }}
      onSelect={handleSelectClientOptions}
    />
  );
};

export default AllocationClientsSelect;
